import { FunctionComponent, useCallback, useContext, useMemo, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import sprite from '../../assets/icons/sprite.svg';
import { ChannelContext, OrganisationContext, UserContext } from '../../context';
import { TCHOP_ROOT_URL } from '../../env';
import { ChannelAppTabAction, OrganisationRole } from '../../services';
import {
  CUSTOM_CLIENT_CONFIG,
  getAppTabHrefByActionType,
  getChannelDomain,
  useAppTabsLabel,
} from '../../shared';

import classNames from 'classnames';
import classes from './MenuAdmin.module.scss';

interface MenuAdminItem {
  label?: string;
  localiseKey?: string;
  iconId?: string;
  highlighted?: boolean;
  href?: string;
  to?: string;
  showSubItems?: boolean;
  setShowSubItems?: (show: boolean) => void;
}

export const MenuAdmin: FunctionComponent = () => {
  const { t } = useTranslation();

  const { pathname } = useLocation();

  const { storyId } = useParams();

  const { getAppTabsLabel } = useAppTabsLabel();

  const { isStaff } = useContext(UserContext).userProfile;

  const { appTabs } = useContext(ChannelContext);

  const { organisation } = useContext(OrganisationContext);

  const channelDomain = useMemo(() => getChannelDomain(), []);

  const roleId = useMemo(() => organisation.role?.id, [organisation.role?.id]);

  const renderOrgSettings = useMemo(
    () =>
      Boolean(
        isStaff || (roleId && [OrganisationRole.OWNER, OrganisationRole.ADMIN].includes(roleId))
      ),
    [isStaff, roleId]
  );

  const renderChats = useMemo(
    () => appTabs.map(({ action }) => action.type).includes(ChannelAppTabAction.CHATS),
    [appTabs]
  );

  const storySubItems = appTabs.filter(({ action: { type } }) =>
    [
      ChannelAppTabAction.STORIES,
      ChannelAppTabAction.STORIES2,
      ChannelAppTabAction.STORIES3,
      ChannelAppTabAction.STORIES4,
      ChannelAppTabAction.STORIES5,
      ChannelAppTabAction.PINNED_STORY,
    ].includes(type)
  );

  const isStorySubItemSelected = useMemo(
    () =>
      [
        '/admin/storyTab1',
        '/admin/storyTab2',
        '/admin/storyTab3',
        '/admin/storyTab4',
        '/admin/storyTab5',
        '/admin/pinnedStoryTab',
        `/admin/storyTab1/${storyId}/items`,
        `/admin/storyTab2/${storyId}/items`,
        `/admin/storyTab3/${storyId}/items`,
        `/admin/storyTab4/${storyId}/items`,
        `/admin/storyTab5/${storyId}/items`,
        `/admin/pinnedStoryTab/${storyId}/items`,
      ].includes(pathname),
    [pathname, storyId]
  );

  const [showStorySubItems, setShowStorySubItems] = useState<boolean>(isStorySubItemSelected);

  const renderStorySubItems = useMemo(
    () => Boolean(showStorySubItems && storySubItems.length),
    [showStorySubItems, storySubItems.length]
  );

  const origin = useMemo(() => {
    if (CUSTOM_CLIENT_CONFIG) {
      const { protocol, host } = new URL(TCHOP_ROOT_URL);

      return `${protocol}//${channelDomain}-${organisation.subdomain}.${host}`;
    }

    return window.location.origin;
  }, [channelDomain, organisation.subdomain]);

  const renderMenuItem = useCallback(
    ({
      label,
      localiseKey,
      iconId,
      to,
      href,
      highlighted,
      showSubItems,
      setShowSubItems,
    }: MenuAdminItem) => {
      const text = label ?? t(`adminLayoutMenu.${localiseKey}`);

      const toggleShowSubItems = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (!setShowSubItems) {
          return;
        }
        event.preventDefault();
        setShowSubItems(!showSubItems);
      };

      return (
        <li key={text}>
          {to && (
            <NavLink
              to={to}
              className={({ isActive }) =>
                classNames(classes['menu__item'], {
                  [classes['menu__item--active']]: isActive,
                  [classes['menu__item--highlighted']]: highlighted,
                })
              }
              {...(to === '/admin' && { end: true })}
            >
              {iconId && (
                <svg className={classes['menu__item-icon']}>
                  <use href={`${sprite}#${iconId}`} />
                </svg>
              )}

              <span className={classes['menu__item-label']}>{text}</span>

              {Boolean(setShowSubItems) && (
                <div className={classes['menu__item-toggle']} onClick={toggleShowSubItems}>
                  <svg className={classes['menu__item-toggle-arrow']}>
                    <use href={`${sprite}#${showSubItems ? 'up' : 'down'}-arrow`} />
                  </svg>
                </div>
              )}
            </NavLink>
          )}
          {href && (
            <a className={classes['menu__item']} href={href} target={'_blank'} rel={'noreferrer'}>
              {iconId && (
                <svg className={classes['menu__item-icon']}>
                  <use href={`${sprite}#${iconId}`} />
                </svg>
              )}
              <span className={classes['menu__item-label']}>{text}</span>
            </a>
          )}
        </li>
      );
    },
    [t]
  );

  return (
    <ul className={classes['menu']}>
      {renderMenuItem({ localiseKey: 'content', iconId: 'home', to: '/admin' })}

      {renderMenuItem({
        localiseKey: 'mixes',
        iconId: 'mixes',
        to: '/admin/storyTab',
        highlighted: isStorySubItemSelected,
        showSubItems: showStorySubItems,
        setShowSubItems: setShowStorySubItems,
      })}

      {renderStorySubItems && (
        <ul className={classes['menu--sub']}>
          {storySubItems.map(({ action: { type } }) => {
            return renderMenuItem({
              to: getAppTabHrefByActionType({ actionType: type }),
              label: getAppTabsLabel(type),
            });
          })}
        </ul>
      )}

      {renderMenuItem({ localiseKey: 'comments', iconId: 'comments', to: '/admin/comments' })}

      {renderChats && renderMenuItem({ localiseKey: 'chats', iconId: 'chats', to: '/admin/chats' })}

      <li className={classes['menu__section']}>{t('common.tools')}</li>

      {renderMenuItem({
        localiseKey: 'integration',
        iconId: 'integration',
        href: `${origin}/integrations`,
      })}

      {!isMobileOnly &&
        renderMenuItem({ localiseKey: 'notification', iconId: 'bell', to: '/admin/notifications' })}

      {renderMenuItem({
        localiseKey: 'user-management',
        iconId: 'avatars-no-bg',
        href: `${origin}/curators`,
      })}

      {!isMobileOnly &&
        renderMenuItem({ localiseKey: 'analytics', iconId: 'analytics', to: '/admin/analytics' })}

      {renderMenuItem({
        localiseKey: 'developers',
        iconId: 'developers',
        href: `${origin}/developers`,
      })}

      {renderMenuItem({
        localiseKey: 'settings',
        iconId: 'settings',
        href: `${origin}/settings`,
      })}

      {renderOrgSettings &&
        renderMenuItem({
          localiseKey: 'orgSettings',
          iconId: 'settings',
          to: '/admin/orgSettings',
        })}
    </ul>
  );
};
